import { Icon } from './icon';

/**
 * Wizard step bar (mockup module 08): a single white pill strip with one cell
 * per step — a circular numbered badge + the step label.
 *
 *   - **active** (`i === current`): brand-filled cell, on-brand text, badge a
 *     translucent white disc.
 *   - **done**   (`i <  current`): dark badge with a `check-circle`, dark text.
 *   - **future** (`i >  current`): muted text, light badge with the index.
 */
export function Stepper({ steps, current }: { steps: string[]; current: number }): JSX.Element {
  return (
    <div className="mb-6 flex gap-0 rounded-[var(--m-r-lg)] bg-[var(--m-surface)] p-2 shadow-[var(--m-shadow-sm)]">
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const cell = active
          ? 'bg-[var(--m-brand)] text-[var(--m-fg-on-brand)]'
          : done
            ? 'text-[var(--m-bg-inverse)]'
            : 'text-[var(--m-fg-subtle)]';
        const badge = active
          ? 'bg-white/25 text-[var(--m-fg-on-brand)]'
          : done
            ? 'bg-[var(--m-bg-inverse)] text-[var(--m-fg-on-brand)]'
            : 'bg-[var(--m-surface-muted)] text-[var(--m-fg-subtle)]';
        return (
          <div
            key={label}
            aria-current={active ? 'step' : undefined}
            className={`flex flex-1 items-center gap-2.5 rounded-[var(--m-r-md)] px-3 py-2.5 text-[12.5px] font-bold transition-colors duration-200 ${cell}`}
          >
            <span
              className={`inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold ${badge}`}
            >
              {done ? <Icon name="check-circle" size={12} /> : i + 1}
            </span>
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
          </div>
        );
      })}
    </div>
  );
}
